import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useScale, scaleW, scaleH } from "@/hooks/useScale";
import LoginCard from "@/components/desktop/LoginCard";
import { getCachedTheme } from "@/utils/themeCache";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { theme, siteCustomization } = useTheme();
  const scale = useScale();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Apply cached site theme on mount for instant theming
  useEffect(() => {
    const cachedSiteTheme = getCachedTheme('site');
    if (cachedSiteTheme) {
            const root = document.documentElement;
      if (cachedSiteTheme.textColor) root.style.setProperty('--text-color', cachedSiteTheme.textColor);
      if (cachedSiteTheme.accentColor) root.style.setProperty('--color-accent', cachedSiteTheme.accentColor);
      if (cachedSiteTheme.cardBg) root.style.setProperty('--card-bg', cachedSiteTheme.cardBg);
      if (cachedSiteTheme.appBg) root.style.setProperty('--app-bg', cachedSiteTheme.appBg);
      if (cachedSiteTheme.navbarBg) root.style.setProperty('--navbar-bg', cachedSiteTheme.navbarBg);
      if (cachedSiteTheme.tabBarBg) root.style.setProperty('--tab-bar-bg', cachedSiteTheme.tabBarBg);
    }
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    if (loading) return;
    if (user) {
      // Already logged in, redirect to feed
      navigate("/feed");
    }
  }, [user, loading, navigate]);

  function handleLoginSuccess() {
    // Redirect to feed after successful login
    navigate("/feed");
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center relative"
      style={{
        background: theme.appBg || 'linear-gradient(135deg, #006064, #00838f, #0097a7)',
        padding: isMobile ? '24px 0' : undefined,
      }}
    >
      {/* Login Card */}
      <LoginCard onSuccess={handleLoginSuccess} />

      {/* Footer area */}
      <div style={{
        position: isMobile ? 'relative' : 'absolute',
        bottom: isMobile ? undefined : 0,
        left: 0,
        right: 0,
        marginTop: isMobile ? '32px' : undefined,
        paddingBottom: isMobile ? '16px' : '24px',
      }}>
        {/* Version Counter */}
        <div
          style={{
            textAlign: 'center',
            fontSize: isMobile ? '13px' : `${scaleW(12, scale.w)}px`,
            color: theme.textColor,
            opacity: 0.5,
            fontWeight: 600,
            marginBottom: isMobile ? '12px' : '8px',
          }}
        >
          v0.8 BETA
        </div>

        {/* Terms Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
          style={{
            fontSize: isMobile ? '13px' : `${scaleW(12, scale.w)}px`,
            color: theme.textColor,
            opacity: 0.6,
            padding: isMobile ? '0 24px' : '0 16px',
          }}
        >
          By continuing, you agree to our{" "}
          <Link to="/terms" className="underline hover:opacity-100" style={{ opacity: 0.8 }}>
            Terms of Service
          </Link>
          {" "}and{" "}
          <Link to="/privacy" className="underline hover:opacity-100" style={{ opacity: 0.8 }}>
            Privacy Policy
          </Link>
        </motion.p>
      </div>
    </div>
  );
}
